<template>
  <div class="my-old-books-page">
    <Header />

    <main class="main-content">
      <div class="container marketplace-container">

        <!-- Compact Unified Toolbar Header (Matching old-book-list) -->
        <div class="ob-compact-toolbar">
          <!-- Top Row: Brand / Title + Primary Action -->
          <div class="toolbar-top-row">
            <div class="toolbar-brand">
              <h1 class="toolbar-title font-serif">Quản lý Gian Hàng Sách Cũ của tôi</h1>
              <span class="toolbar-dot">•</span>
              <span class="toolbar-subtitle">Quản lý và theo dõi các bài đăng gian hàng / combo sách cũ bạn đang nhượng lại</span>
            </div>

            <div class="toolbar-top-actions">
              <span class="toolbar-count-badge">
                <strong>{{ totalElements }}</strong> gian hàng
              </span>

              <button @click="goToCreateBook" class="btn-shopee-post">
                <icon icon="fa-solid fa-plus" class="btn-post-icon" />
                <span>Đăng bán gian hàng mới</span>
              </button>
            </div>
          </div>

          <!-- Bottom Row: Integrated Compact Search & Status Filter Toolbar -->
          <div class="toolbar-controls-row">
            <!-- Search within my listings -->
            <div class="toolbar-search-box">
              <icon icon="fa-solid fa-magnifying-glass" class="search-icon" />
              <input 
                type="text" 
                v-model="searchKeyword" 
                placeholder="Tìm tên gian hàng, tên sách, tác giả trong bài đăng của bạn..." 
                class="toolbar-search-input"
              />
              <button v-if="searchKeyword" type="button" class="btn-clear-icon" @click="searchKeyword = ''" title="Xóa từ khóa">
                <icon icon="fa-solid fa-xmark" />
              </button>
            </div>

            <!-- Status Chips Filter -->
            <div class="shopee-chips">
              <button 
                class="chip-item"
                :class="{ active: selectedStatus === null }"
                @click="filterStatus(null)"
              >
                Tất cả ({{ statusCounts.all || 0 }})
              </button>
              <button 
                class="chip-item"
                :class="{ active: selectedStatus === 0 }"
                @click="filterStatus(0)"
              >
                Đang bán ({{ statusCounts['0'] || 0 }})
              </button>
              <button 
                class="chip-item"
                :class="{ active: selectedStatus === 1 }"
                @click="filterStatus(1)"
              >
                Đã nhận cọc ({{ statusCounts['1'] || 0 }})
              </button>
              <button 
                class="chip-item"
                :class="{ active: selectedStatus === 2 }"
                @click="filterStatus(2)"
              >
                Đã bán ({{ statusCounts['2'] || 0 }})
              </button>
              <button 
                class="chip-item"
                :class="{ active: selectedStatus === 3 }"
                @click="filterStatus(3)"
              >
                Tạm ngưng ({{ statusCounts['3'] || 0 }})
              </button>
              <button 
                class="chip-item"
                :class="{ active: selectedStatus === 4 }"
                @click="filterStatus(4)"
              >
                Đã hủy ({{ statusCounts['4'] || 0 }})
              </button>
            </div>

            <!-- Results Counter Badge on right -->
            <div class="toolbar-results-badge ms-auto">
              <span class="results-badge">
                Tìm thấy <strong>{{ totalElements }}</strong> gian hàng
              </span>
            </div>
          </div>
        </div>

        <!-- Loading Skeletons -->
        <div v-if="isLoading" class="shopee-loading-section">
          <div class="shopee-loading-indicator">
            <icon icon="fa-solid fa-circle-notch" class="shopee-loading-spinner" />
            <span>Đang tải danh sách gian hàng của bạn...</span>
          </div>
          <div class="shopee-grid">
            <div v-for="n in 6" :key="n" class="skeleton-shopee-card">
              <div class="skeleton-img"></div>
              <div class="skeleton-text line1"></div>
              <div class="skeleton-text line2"></div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredBooths.length === 0" class="shopee-empty-state">
          <div class="empty-icon-circle">
            <icon icon="fa-solid fa-boxes-packing" class="empty-icon" />
          </div>
          <h3>{{ selectedStatus !== null || searchKeyword ? 'Không tìm thấy gian hàng phù hợp' : 'Bạn chưa đăng bán gian hàng sách cũ nào' }}</h3>
          <p>Đăng nhượng lại những cuốn sách đã đọc để chia sẻ tri thức và tìm chủ mới cho sách của bạn!</p>
          <button @click="goToCreateBook" class="btn-shopee-post mt-3">
            <icon icon="fa-solid fa-plus" class="me-1" /> Đăng bán gian hàng đầu tiên
          </button>
        </div>

        <!-- MY BOOTHS GRID -->
        <div v-else class="shopee-grid">
          <div 
            v-for="booth in filteredBooths" 
            :key="booth.id"
            class="shopee-card"
            @click="goToDetail(booth.id)"
          >
            <!-- Cover Frame -->
            <div class="shopee-card-img" @click.stop="openQuickView(booth)">
              <img 
                :src="getCoverUrl(booth.coverImage || (booth.books && booth.books[0] && booth.books[0].cover))" 
                :alt="booth.name" 
                loading="lazy" 
                @error="handleImageError" 
              />
              
              <!-- Badges Container on Top-Left -->
              <div class="shopee-badge-stack">
                <span class="shopee-cond-tag" :class="{ 'is-combo': booth.totalBooks > 1 }">
                  <icon :icon="booth.totalBooks > 1 ? 'fa-solid fa-layer-group' : 'fa-solid fa-book'" class="me-1" />
                  {{ booth.totalBooks > 1 ? ('Combo ' + booth.totalBooks + ' cuốn') : '1 cuốn' }}
                </span>
                <span class="shopee-sale-type-tag" :class="booth.saleType === 1 ? 'type-combo' : 'type-single'">
                  {{ booth.saleType === 1 ? 'Mua combo' : 'Mua lẻ/cả bộ' }}
                </span>
              </div>

              <!-- Sale Off Tag if any -->
              <div v-if="booth.discountRate && booth.discountRate > 0" class="my-booth-sale-tag">
                -{{ booth.discountRate }}%
              </div>

              <!-- Quick View Button -->
              <button class="shopee-btn-quick" @click.stop="openQuickView(booth)" title="Xem nhanh thông tin">
                <icon icon="fa-solid fa-eye" /> Xem nhanh
              </button>
            </div>

            <!-- Card Body -->
            <div class="shopee-card-body">
              <h3 class="shopee-card-title font-serif" :title="booth.name" @click="goToDetail(booth.id)">
                {{ booth.name }}
              </h3>

              <!-- Authors & Categories Meta -->
              <div class="shopee-card-meta-row" v-if="getAuthorsList(booth).length > 0">
                <span class="meta-item-author">
                  <icon icon="fa-solid fa-feather-pointed" class="me-1" />{{ getAuthorsList(booth).slice(0, 2).join(', ') }}
                </span>
              </div>

              <!-- Books Inside Summary (only when >= 2 books) -->
              <p class="shopee-card-desc" v-if="booth.books && booth.books.length > 1">
                <strong class="text-emerald-700">Gồm:</strong> {{ booth.books.map(b => b.name).join(' • ') }}
              </p>
              
              <!-- Price Row -->
              <div class="shopee-card-price-row mt-auto">
                <div class="d-flex align-items-baseline gap-2 flex-wrap">
                  <span class="shopee-price">{{ formatPrice(booth.price) }}</span>
                  <span v-if="booth.originalPrice && booth.originalPrice > booth.price" class="shopee-original-price">
                    {{ formatPrice(booth.originalPrice) }}
                  </span>
                </div>
              </div>

              <!-- Card Footer: Edit Button & Status -->
              <div class="shopee-public-footer mt-2 pt-2 border-top">
                <button 
                  type="button" 
                  class="card-edit-btn" 
                  :disabled="!canEditBooth(booth)" 
                  @click.stop="goToEditBooth(booth)"
                  :title="!canEditBooth(booth) ? 'Bài đăng ở trạng thái Đã bán hoặc Đã hủy không thể chỉnh sửa' : 'Chỉnh sửa bài đăng'"
                >
                  <icon icon="fa-solid fa-pen-to-square" class="me-1" /> Chỉnh sửa
                </button>
                <span class="public-status-tag" :class="'status-tag-' + booth.status">
                  <span class="status-dot"></span>
                  {{ getListingStatusLabel(booth.status) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="shopee-pagination">
          <ul class="pagination-list">
            <li :class="{ disabled: currentPage === 0 }">
              <button class="page-btn" @click="fetchMyBooths(currentPage - 1)"><icon icon="fa-solid fa-chevron-left" /></button>
            </li>
            <li v-for="p in visiblePages" :key="p" :class="{ active: p === currentPage }">
              <span v-if="p === '...'" class="page-dots">…</span>
              <button v-else class="page-btn" @click="fetchMyBooths(p)">{{ p + 1 }}</button>
            </li>
            <li :class="{ disabled: currentPage >= totalPages - 1 }">
              <button class="page-btn" @click="fetchMyBooths(currentPage + 1)"><icon icon="fa-solid fa-chevron-right" /></button>
            </li>
          </ul>
        </div>

      </div>
    </main>

    <!-- Shared Quick View Drawer & Zoom Component (Owner Mode) -->
    <OldBookQuickDrawer 
      :booth="drawerBooth" 
      mode="owner" 
      :can-edit="drawerBooth ? canEditBooth(drawerBooth) : true" 
      @close="drawerBooth = null" 
      @edit="goToEditBooth" 
    />

    <Footer />
  </div>
</template>

<script src="./my-old-book-list.js"></script>
<style scoped src="./my-old-book-list.css"></style>
